import { FC, ReactNode } from "react";
import styled, { css } from "styled-components";

/**
 * SOREMO 共通ステータスバッジ（旧 `status-label/StatusLabel` の後継）。
 *
 * 目的: **システム上の状態表示**。クリックできない表示専用。
 *  - 進行中リストの「旧環境」表示（chip）
 *  - DM スレッド右下のステータス（inline）
 *  - 招待中 / 本人確認 / NDA 締結済み / 保留（overlay）等
 *
 * タグ系コンポーネントの使い分けは `FilterChip` のコメントを参照。
 * ※ テイクバッジ（`label/TakeLabel` の垂れタブ）はメディア上専用の表示形
 *   として別コンポーネントのまま維持する。
 *
 * 色の使い分け: ユーザーにアクションを求める場合は soremo-blue、
 * そうでない場合は soremo-light-gray（`inline` は `highlighted` で切替）。
 * 失敗・エラーなど注意を引く必要がある状態は soremo-green
 * （SOREMO では警告色として緑を使う。`.u-text-green` / `.text-danger` と同色）。
 */
export type StatusBadgeVariant = "chip" | "inline" | "overlay";
export type StatusBadgeChipColor = "blue" | "light-gray" | "green" | "border";
export type StatusBadgeSize = "small" | "medium";

export interface StatusBadgeProps {
  /** 表示テキスト（or ReactNode）。 */
  label: ReactNode;
  /**
   * 表示形態。
   * - `chip`（default）: 枠付きチップ。プロジェクトバナー・本人確認等
   * - `inline`: テキストインライン。スレッド・ロール一覧等
   * - `overlay`: 親要素 (`position: relative`) の左上にオーバーレイ
   */
  variant?: StatusBadgeVariant;
  /** chip variant の色トークン。default: `"border"` */
  color?: StatusBadgeChipColor;
  /** chip variant のサイズ。`small`=8px, `medium`=11px。default: `"medium"` */
  size?: StatusBadgeSize;
  /** chip variant の左アイコン（Material Symbols 名） */
  leftIcon?: string;
  /** chip variant の右アイコン（Material Symbols 名） */
  rightIcon?: string;
  /** inline variant の強調 ON/OFF。default: `false` */
  highlighted?: boolean;
  /** 追加クラス。utility class の付与等に使う */
  className?: string;
}

const sizeMap: Record<StatusBadgeSize, string> = {
  small: "8px",
  medium: "11px",
};

const chipColorStyles = (color: StatusBadgeChipColor) => {
  switch (color) {
    case "blue":
      return css`
        color: var(--soremo-blue);
        border-color: transparent;
        background-color: transparent;
      `;
    case "light-gray":
      return css`
        color: var(--soremo-light-gray);
        border-color: transparent;
        background-color: transparent;
      `;
    case "green":
      return css`
        color: var(--soremo-green);
        border-color: transparent;
        background-color: transparent;
      `;
    case "border":
    default:
      return css`
        color: var(--soremo-gray);
        border-color: var(--soremo-border);
        background-color: var(--soremo-background);
      `;
  }
};

const ChipBadge = styled.div<{
  $color: StatusBadgeChipColor;
  $size: StatusBadgeSize;
}>`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ $size }) => sizeMap[$size]};
  line-height: 1;
  border-radius: 4px;
  border: 1px solid transparent;
  padding: 4px 8px;
  ${({ $color }) => chipColorStyles($color)}
`;

const InlineBadge = styled.span.attrs({
  className: "bodytext-11 u-line-height-100",
})<{ $highlighted: boolean }>`
  display: inline-block;
  font-size: 11px;
  line-height: 1;
  color: ${({ $highlighted }) =>
    $highlighted ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  border: ${({ $highlighted }) =>
    $highlighted ? "1px solid var(--soremo-blue)" : "none"};
  /* 「旧環境」chip と同じ 4px 角丸に統一 */
  border-radius: 4px;
  padding: ${({ $highlighted }) => ($highlighted ? "4px 8px" : "0")};
`;

const OverlayBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  padding: 4px 16px;
  border: 1px solid #fff;
  border-radius: 0;
  font-size: 11px;
  line-height: 1.2;
  color: #fff;
  pointer-events: none;
`;

const StatusBadge: FC<StatusBadgeProps> = ({
  label,
  variant = "chip",
  color = "border",
  size = "medium",
  leftIcon,
  rightIcon,
  highlighted = false,
  className,
}) => {
  if (variant === "inline") {
    return (
      <InlineBadge className={className} $highlighted={highlighted}>
        {label}
      </InlineBadge>
    );
  }

  if (variant === "overlay") {
    return <OverlayBadge className={className}>{label}</OverlayBadge>;
  }

  return (
    <ChipBadge className={className} $color={color} $size={size}>
      {leftIcon && (
        <span
          className="material-symbols-rounded"
          style={{ fontSize: "24px" }}
        >
          {leftIcon}
        </span>
      )}
      {label}
      {rightIcon && (
        <span
          className="material-symbols-rounded"
          style={{ fontSize: "24px" }}
        >
          {rightIcon}
        </span>
      )}
    </ChipBadge>
  );
};

export default StatusBadge;
